import { Ionicons } from "@expo/vector-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "expo-router";
import { useState } from "react";
import {
    Alert,
    FlatList,
    Pressable,
    StyleSheet,
    Text,
    TextInput,
    useColorScheme,
    View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import {
  despesasRepository,
  formatarDataLocal,
  parseValorDespesa,
} from "../repositories/despesasRepository";
import { Despesa } from "../types/database";
import { confirmarAcaoWebOuNativo } from "../utils/confirmarAcaoWebOuNativo";

const formatarData = (data: string) => {
  if (!data) return "";

  const [ano, mes, dia] = data.split("-");
  if (!ano || !mes || !dia) return data;

  return `${dia}/${mes}/${ano}`;
};

export default function TelaDespesas() {
  const queryClient = useQueryClient();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const styles = createStyles(isDark);
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [dataCompra, setDataCompra] = useState(() =>
    formatarDataLocal(new Date()),
  );
  const hoje = new Date();

  const {
    data: despesas,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["despesas-mes", hoje.getFullYear(), hoje.getMonth()],
    queryFn: despesasRepository.listarDoMesAtual,
  });

  const criarDespesaMutation = useMutation({
    mutationFn: () => {
      const valorNumero = parseValorDespesa(valor);

      if (!Number.isFinite(valorNumero) || valorNumero <= 0) {
        throw new Error("Informe um valor maior que zero.");
      }

      return despesasRepository.criar(descricao, valorNumero, dataCompra);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["despesas-mes"] });
      await refetch();
      setDescricao("");
      setValor("");
      setDataCompra(formatarDataLocal(new Date()));
    },
    onError: (err: Error) => {
      Alert.alert("Erro", err.message || "Não foi possível salvar a despesa.");
    },
  });

  const deletarDespesaMutation = useMutation({
    mutationFn: (id: string) => despesasRepository.deletar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["despesas-mes"] });
    },
    onError: (err: Error) => {
      Alert.alert("Erro", err.message || "Não foi possível excluir a despesa.");
    },
  });

  const totalMes = (despesas ?? []).reduce((total, despesa) => {
    return total + Number(despesa.valor || 0);
  }, 0);

  function handleSalvar() {
    if (!descricao.trim()) {
      Alert.alert("Campo obrigatório", "Informe a descrição da despesa.");
      return;
    }

    const valorNumero = parseValorDespesa(valor);
    if (!Number.isFinite(valorNumero) || valorNumero <= 0) {
      Alert.alert("Campo obrigatório", "Informe um valor maior que zero.");
      return;
    }

    criarDespesaMutation.mutate();
  }

  function handleExcluir(despesa: Despesa) {
    confirmarAcaoWebOuNativo({
      titulo: "Confirmar exclusão",
      mensagem: `Excluir a despesa "${despesa.descricao}"?`,
      onConfirmar: () => deletarDespesaMutation.mutate(despesa.id),
      destructive: true,
      textoConfirmar: "Excluir",
    });
  }

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <View style={styles.header}>
        <View style={styles.headerTitleWrap}>
          <Link href="/" asChild>
            <Pressable style={styles.voltarButton} hitSlop={8}>
              <Ionicons name="arrow-back" size={20} color="#2563eb" />
            </Pressable>
          </Link>
          <Text style={styles.titulo}>Despesas</Text>
        </View>
      </View>

      <View style={styles.formCard}>
        <Text style={styles.sectionTitle}>Nova despesa</Text>

        <Text style={styles.label}>Descrição</Text>
        <TextInput
          style={styles.input}
          value={descricao}
          onChangeText={setDescricao}
          placeholder="Ex.: Pote de açaí"
        />

        <Text style={styles.label}>Valor gasto</Text>
        <TextInput
          style={styles.input}
          value={valor}
          onChangeText={setValor}
          placeholder="0.00"
          keyboardType="decimal-pad"
        />

        <Text style={styles.label}>Data</Text>
        <TextInput
          style={styles.input}
          value={dataCompra}
          onChangeText={setDataCompra}
          placeholder="YYYY-MM-DD"
        />

        <Pressable
          style={[
            styles.botaoSalvar,
            criarDespesaMutation.isPending && styles.botaoDesabilitado,
          ]}
          onPress={handleSalvar}
          disabled={criarDespesaMutation.isPending}
        >
          <Text style={styles.botaoSalvarTexto}>
            {criarDespesaMutation.isPending
              ? "Salvando..."
              : "Adicionar despesa"}
          </Text>
        </Pressable>
      </View>

      <View style={styles.resumoCard}>
        <Text style={styles.resumoLabel}>Total do mês</Text>
        <Text style={styles.resumoValor}>R$ {totalMes.toFixed(2)}</Text>
      </View>

      <Text style={styles.sectionTitle}>Histórico</Text>

      {isLoading ? (
        <Text style={styles.vazioTexto}>Carregando despesas...</Text>
      ) : (
        <FlatList
          data={despesas ?? []}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.lista}
          refreshing={false}
          onRefresh={() => refetch()}
          ListEmptyComponent={
            <Text style={styles.vazioTexto}>
              Nenhuma despesa registrada neste mês.
            </Text>
          }
          renderItem={({ item }) => (
            <View style={styles.itemCard}>
              <View style={styles.itemConteudo}>
                <Text style={styles.itemDescricao}>{item.descricao}</Text>
                <Text style={styles.itemData}>
                  {formatarData(item.data_compra)}
                </Text>
              </View>

              <View style={styles.itemAcoes}>
                <Text style={styles.itemValor}>
                  R$ {Number(item.valor || 0).toFixed(2)}
                </Text>
                <Pressable
                  style={styles.botaoExcluir}
                  onPress={() => handleExcluir(item)}
                  disabled={deletarDespesaMutation.isPending}
                >
                  <Ionicons name="trash-outline" size={18} color="#fff" />
                </Pressable>
              </View>
            </View>
          )}
        />
      )}
    </SafeAreaView>
  );
}

function createStyles(isDark: boolean) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: isDark ? "#121212" : "#f5f5f5",
      paddingHorizontal: 20,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: 8,
      paddingBottom: 12,
    },
    headerTitleWrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },
    voltarButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: isDark ? "#1E1E1E" : "#eff6ff",
      justifyContent: "center",
      alignItems: "center",
    },
    titulo: {
      fontSize: 28,
      fontWeight: "bold",
      color: isDark ? "#FFFFFF" : "#111",
    },
    formCard: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#e5e7eb",
      padding: 16,
      marginBottom: 16,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "700",
      color: isDark ? "#FFFFFF" : "#111827",
      marginBottom: 12,
    },
    label: {
      fontSize: 13,
      fontWeight: "600",
      color: isDark ? "#E5E7EB" : "#374151",
      marginBottom: 6,
    },
    input: {
      backgroundColor: isDark ? "#1F2937" : "#f9fafb",
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderWidth: 1,
      borderColor: isDark ? "#374151" : "#d1d5db",
      marginBottom: 12,
      fontSize: 15,
      color: isDark ? "#FFFFFF" : "#111827",
    },
    botaoSalvar: {
      backgroundColor: "#2563eb",
      borderRadius: 10,
      paddingVertical: 12,
      alignItems: "center",
      justifyContent: "center",
      marginTop: 8,
    },
    botaoSalvarTexto: { color: "#fff", fontWeight: "700", fontSize: 15 },
    botaoDesabilitado: { opacity: 0.7 },
    resumoCard: {
      backgroundColor: isDark ? "#3B1E1E" : "#fef2f2",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? "#7F3D3D" : "#fecaca",
      padding: 14,
      marginBottom: 12,
    },
    resumoLabel: {
      fontSize: 12,
      color: isDark ? "#FCA5A5" : "#4b5563",
      fontWeight: "600",
    },
    resumoValor: {
      fontSize: 22,
      fontWeight: "800",
      color: isDark ? "#FCA5A5" : "#b91c1c",
      marginTop: 4,
    },
    lista: { paddingBottom: 20 },
    itemCard: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderRadius: 12,
      padding: 12,
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#e5e7eb",
      marginBottom: 10,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    itemConteudo: { flex: 1, marginRight: 8 },
    itemDescricao: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? "#FFFFFF" : "#111827",
    },
    itemData: {
      fontSize: 12,
      color: isDark ? "#AAAAAA" : "#6b7280",
      marginTop: 2,
    },
    itemAcoes: { alignItems: "flex-end" },
    itemValor: {
      fontSize: 16,
      fontWeight: "800",
      color: isDark ? "#FFFFFF" : "#111827",
      marginBottom: 6,
    },
    botaoExcluir: {
      backgroundColor: "#dc2626",
      width: 34,
      height: 34,
      borderRadius: 8,
      alignItems: "center",
      justifyContent: "center",
    },
    vazioTexto: {
      color: isDark ? "#AAAAAA" : "#6b7280",
      fontSize: 14,
      paddingVertical: 18,
    },
  });
}
